import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  text: string;
  /** True while the model is still emitting reasoning deltas for this part. */
  streaming?: boolean;
}

/**
 * Renders an extended-thinking block. While streaming, defaults to expanded
 * with a live pulse so the user can watch the model think; once streaming
 * stops, collapses to "Thought for Xs" summary that the user can re-open.
 *
 * Elapsed time is captured as the wall-clock duration the block was in the
 * streaming state — close enough to "how long did the model think" without
 * needing a token-budget read from the provider.
 */
export function ReasoningBlock({ text, streaming = false }: Props) {
  const [userToggled, setUserToggled] = useState<boolean | null>(null);
  const [, force] = useState(0);

  // Capture the wall-clock duration the block spent streaming.
  // We lock `endedAt` once `streaming` flips false so the summary stays stable.
  const startedAt = useRef<number | null>(null);
  const endedAt = useRef<number | null>(null);

  useEffect(() => {
    if (streaming) {
      if (startedAt.current === null) startedAt.current = Date.now();
      endedAt.current = null;
      // Tick once a second so the live "Thinking 3s…" label updates.
      const t = setInterval(() => force((n) => n + 1), 1000);
      return () => clearInterval(t);
    }
    if (startedAt.current !== null && endedAt.current === null) {
      endedAt.current = Date.now();
    }
  }, [streaming]);

  // Default-expanded while streaming; default-collapsed once done. User
  // toggles override the default.
  const open = userToggled ?? streaming;
  const elapsed =
    startedAt.current !== null
      ? Math.max(0, Math.round(((endedAt.current ?? Date.now()) - startedAt.current) / 1000))
      : null;

  const summary = streaming
    ? elapsed !== null && elapsed > 0
      ? `Thinking · ${elapsed}s`
      : 'Thinking…'
    : elapsed !== null && elapsed > 0
      ? `Thought for ${elapsed}s`
      : 'Thought';

  return (
    <div className="my-1.5 rounded-md border border-border bg-surface/60">
      <button
        type="button"
        onClick={() => setUserToggled((u) => (u === null ? !open : !u))}
        className="focus-ring flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-fg-muted hover:text-fg"
        aria-expanded={open}
      >
        <Brain className="h-3 w-3 shrink-0" />
        <ChevronRight
          className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-90')}
        />
        <span className="font-medium">{summary}</span>
        {streaming && (
          <span
            className="ml-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
            style={{ animation: 'pulse 1.4s ease-in-out infinite' }}
          />
        )}
        {!open && !streaming && text && (
          <span className="ml-1 truncate font-normal text-fg-subtle">
            {text.length > 100 ? text.slice(0, 100).trimEnd() + '…' : text}
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-2.5 pl-9 pt-0">
          <div className="whitespace-pre-wrap text-[12.5px] italic leading-relaxed text-fg-muted">
            {text || (streaming ? '…' : '')}
          </div>
        </div>
      )}
    </div>
  );
}
